import {
  SubmissionFormFileInput,
  SubmissionFormInput,
  SubmissionFormMultiSelect,
  SubmissionFormRadio,
  SubmissionFormSelect,
  SubmissionFormTeamInput,
} from './inputs';
import { GENRES, SUB_GENRES } from './schema';

const Rows: React.FC = ({ children }) => (
  <div className="flex flex-col gap-2">{children}</div>
);

const Cols: React.FC = ({ children }) => (
  <div className="grid lg:grid-cols-2 grid-cols-1 gap-2 lg:gap-8 items-end">
    {children}
  </div>
);

export const IntroStep = () => (
  <div className="flex flex-col gap-6">
    <p>
      Are you a filmmaker with a vision? Submit a project proposal for a chance
      to have your concept financed through NFTs
    </p>
    <p>
      When you submit a project, our Selection Panel reviews it and contacts the
      successful projects to create and negotiate campaigns for funding in the
      Mogul Showcase
    </p>
    <a
      href="/files/project-onboarding.pdf"
      download="Mogul Productions Project Onboarding"
      className="underline"
    >
      Review our onboarding guide here
    </a>
  </div>
);

export const PersonStep = () => (
  <Rows>
    <Cols>
      <SubmissionFormInput name={'name'} />
      <SubmissionFormInput name={'email'} />
    </Cols>
    <Cols>
      <SubmissionFormInput name={'phone'} />
      <SubmissionFormInput name={'city'} />
    </Cols>
    <Cols>
      <SubmissionFormInput name={'state'} label="State/Prov" />
      <SubmissionFormInput name={'country'} />
    </Cols>
  </Rows>
);

export const ProjectStep = () => (
  <Rows>
    <Cols>
      <SubmissionFormInput name={'projectName'} label="Film/Project Name" />
      <SubmissionFormSelect
        name={'genre'}
        label="Primary Genre"
        options={GENRES.map(genre => ({ value: genre, label: genre }))}
      />
    </Cols>
    <Cols>
      <SubmissionFormMultiSelect
        name={'subGenres'}
        label="Sub Genres"
        options={SUB_GENRES.map(genre => ({ value: genre, label: genre }))}
      />
      <SubmissionFormInput
        name={'screenwriter'}
        label="Who is the screenwriter?"
      />
    </Cols>
    <SubmissionFormInput
      name={'attachments'}
      label="Notable attachments to the project. Producer/Director/Writer/Actors?"
      isTextarea
    />
  </Rows>
);

export const PitchStep = () => (
  <Rows>
    <SubmissionFormInput
      name={'screenplay'}
      label="50-100 word longline of the screenplay:"
      isTextarea
    />
    <SubmissionFormInput
      name={'synopsis'}
      label="100-300 word Synopsis:"
      isTextarea
    />
    <SubmissionFormInput name={'pageCount'} label="What is the page count?" />
  </Rows>
);

export const DetailsStep = () => (
  <Rows>
    <SubmissionFormRadio
      name={'original'}
      label="Is the project original material or adapted from a different source?"
      options={[
        {
          value: 'Original',
          label: 'Original material',
        },
        {
          value: 'Adaptation',
          label: 'Adaptation',
        },
      ]}
    />
    <SubmissionFormInput name={'era'} label="What era is it set in?" />
    <SubmissionFormInput
      name={'location'}
      label="What is the primary shoot location?"
    />
    <SubmissionFormInput name={'audience'} label="Target audience?" />
  </Rows>
);

export const BusinessDetailsStep = () => (
  <Rows>
    <SubmissionFormInput
      name={'stakeholders'}
      label="Background on Stakeholders: IMDB/Other"
    />
    <Cols>
      <SubmissionFormInput
        name={'status'}
        label="Current status of the film:"
      />
      <SubmissionFormInput
        name={'estimatedBudget'}
        label="Estimated Budget (USD)"
      />
    </Cols>
    <Cols>
      <SubmissionFormInput
        name={'budgetPercent'}
        label="What % of film budget do you have?"
      />
      <SubmissionFormInput
        name={'mogulBudget'}
        label="Budget would you like to finance on Mogul?"
      />
    </Cols>
    <Cols>
      <SubmissionFormInput
        name={'financialMilestones'}
        label="Financial Milestones: ie: X amount to greenlight:"
      />
      <SubmissionFormInput name={'goals'} label="Goals of the Project?" />
    </Cols>
    <SubmissionFormInput
      name={'distributor'}
      label="Is there a sales agent or distributor attached, or any domestic or foreign pre-sales in place on the project at this stage?"
    />
  </Rows>
);

export const ContinuedBusinessDetailsStep = () => (
  <Rows>
    <SubmissionFormInput
      name={'sellingPoints'}
      label="Film/Project Selling Points + Roadmap:"
    />
    <SubmissionFormInput
      name={'keyDates'}
      label="Key dates of project and ETA on public launch/announcement:"
    />

    <Cols>
      <SubmissionFormInput
        name={'distributionStrategy'}
        label="What is your distribution and GTM strategy for the film?"
      />
      <SubmissionFormInput
        name={'attachedArtist'}
        label="Artist attached to project:"
      />
    </Cols>
    <Cols>
      <SubmissionFormInput name={'synopsisLink'} label="Link to synopsis" />
      <SubmissionFormInput name={'scriptLink'} label="Link to script" />
    </Cols>
    <Cols>
      <SubmissionFormInput
        name={'charactersLink'}
        label="Link to character descriptions"
      />
      <SubmissionFormInput
        name={'storyLink'}
        label="Link to story background"
      />
    </Cols>
    <SubmissionFormInput
      name={'promoteHelp'}
      label="Who from your project will help promote the campaign? Any specific actors/talent/producers?"
    />
    <SubmissionFormInput
      name={'utilities'}
      label="What perks/utility are you able to offer the NFT holders? For eg, X# of red carpet premiere tickets, Facetime with cast, royalties from movie, BTS footage, signed posters/memorabilia etc."
    />
    <SubmissionFormInput
      name={'initialIdeas'}
      label="Do you have any initial ideas on how you would like to position the creation of the NFTs i.e pre/post production/distribution?"
    />
    <SubmissionFormInput
      name={'anythingElse'}
      label="Anything else you’d like us to know?"
      isTextarea
    />
  </Rows>
);

export const UploadAssetsStep = () => (
  <Rows>
    <Cols>
      <SubmissionFormFileInput
        name="reel"
        label="Sizzle Reel:"
        formatsInfo="Mp4 (size limit 10mb)"
        validFormats={['video/mp4']}
      />
      <SubmissionFormFileInput
        name="image"
        label="Main image:"
        formatsInfo=".jpg / .png/ .gif (size limit 10mb)"
        validFormats={['image/jpeg', 'image/png', 'image/gif']}
      />
    </Cols>
    <Cols>
      <SubmissionFormFileInput
        name="header"
        label="Header Banner:"
        formatsInfo=".jpg / .png/ .gif (size limit 10mb) 1680x450"
        validFormats={['image/jpeg', 'image/png', 'image/gif']}
        validRes={{ height: 450, width: 1680 }}
      />
      <SubmissionFormFileInput
        name="footer"
        label="Footer Banner:"
        formatsInfo=".jpg / .png/ .gif (size limit 10mb) 1680x450"
        validFormats={['image/jpeg', 'image/png', 'image/gif']}
        validRes={{ height: 450, width: 1680 }}
      />
    </Cols>
    <SubmissionFormTeamInput />
  </Rows>
);
